import { useCallback, useEffect, useRef, useState } from 'react';
import type { YouTubeEvent } from 'react-youtube';
import type { Nullable } from 'corgi-types';

import type { DispatchState } from '../../types/React';
import { getEnterHandler } from './../../utils/keyboard';

interface UseHandlersOptions {
  isPreloaded: boolean;
  setIsPreloaded: DispatchState<boolean>;
  setOverlayed: DispatchState<boolean>;
}

export const enum YouTubePlayerState {
  Playing = 1,
}

export interface YouTubePlayerRef extends Record<'pauseVideo' | 'playVideo', () => void> {
  getPlayerState: () => YouTubePlayerState;
}

export const useHandlers = ({ isPreloaded, setIsPreloaded, setOverlayed }: UseHandlersOptions) => {
  const playerRef = useRef<YouTubePlayerRef>(null);
  const handleClick = useCallback(() => {
    if (!isPreloaded) {
      setIsPreloaded(true);
    }
  }, []);
  const handleReadyState = useCallback(({ target }: YouTubeEvent) => {
    playerRef.current = target;
    setOverlayed(false);
  }, []);
  const handlePlayState = useCallback(() => {
    setOverlayed(false);
  }, []);
  const handlePauseState = useCallback(() => {
    setOverlayed(false);
    playerRef.current?.pauseVideo();
  }, []);

  return {
    onClick: handleClick,
    onKeyDown: getEnterHandler(handleClick),
    onReady: handleReadyState,
    onPlay: handlePlayState,
    onPause: handlePauseState,
  };
};

export const useActiveEffect = (isActive: boolean, onPause: () => void) => {
  useEffect(() => {
    if (!isActive) {
      onPause();
    }
  }, [isActive]);
};

type VideoPlayerHandler = Nullable<() => void>;

export type SetVideoPlayer = ReturnType<typeof useVideoPlay>['setVideoPlayer'];

export const useVideoPlay = () => {
  const [videoPlayHandler, setVideoPlayHandler] = useState<VideoPlayerHandler>(null);

  const setVideoPlayer = (callback: () => void) => {
    setVideoPlayHandler(callback);
  };

  const toggleVideoPlay = () => {
    videoPlayHandler?.();
  };

  return { toggleVideoPlay, setVideoPlayer };
};
